import axios from "axios";


import { API_BASE_URL } from "../config";

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE_URL
});

// Add request interceptor to inject token
api.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});
 
// Add response interceptor to handle token expiration
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/auth?error=session_expired";
    }
    return Promise.reject(error);
  }
);

// Authenticate user (handles both register and login based on the route)
export const authUser = (data, type) => {
  const endpoint = type === "register" ? "register" : "login";
  return api.post(`/${endpoint}`, data);
};

// PERFORMANCE ENHANCEMENT: Fetch products with pagination and filtering
export const fetchProducts = async ({ queryKey }) => {
  // react-query passes the queryKey array to the fetch function.
  // We destructure it to get our parameters.
  const [_key, { page, limit, searchTerm, category, brand, minPrice, maxPrice, sort }] = queryKey;

  try {
    // Use a plain object to build parameters
    const queryParamsObj = {
      page: page || 1,
      limit: limit || 12,
    };
    if (searchTerm) queryParamsObj.searchTerm = searchTerm;
    if (category) queryParamsObj.category = category;
    if (brand) queryParamsObj.brand = brand;
    // Ensure empty/undefined values are not sent if they are not intended filters
    if (minPrice !== "" && minPrice !== undefined) queryParamsObj.minPrice = minPrice;
    if (maxPrice !== "" && maxPrice !== undefined) queryParamsObj.maxPrice = maxPrice;
    if (sort) queryParamsObj.sort = sort;

    // Manually create URLSearchParams from the object
    const params = new URLSearchParams(queryParamsObj);

    // Manually construct the full URL string using the baseURL from the axios instance
    // This ensures the '?' is correctly appended before query parameters.
    const fullUrl = `${api.defaults.baseURL}/api/products?${params.toString()}`;

    // Make the GET request using the manually constructed URL
    const response = await api.get(fullUrl);
    return response.data; // Assuming backend returns { products: [], totalPages: X, currentPage: Y }
  } catch (error) {
    console.error('Error fetching products:', error.message);
    throw new Error(error.message);
  }
};

// Add a new product
export const addProduct = async (formData) => {
  try {
        const response = await api.post(`/api/products`, formData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    console.error("Error adding product:", error.response || error);
    throw error;
  }
};

// Fetch product by ID
export const fetchProductById = async (id) => {
  try {
    const response = await api.get(`/api/products/${id}`);
    return response.data.product;
  } catch (error) {
    console.error("Error fetching product by ID:", error.response || error);
    throw error;
  }
};


// Place an order
export const placeOrder = async (data) => {
  try {
        const response = await api.post(`/api/orders`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    console.error("Error placing order:", error.response || error);
    throw error;
  }
};

// Fetch all orders for the logged-in user
export const fetchOrders = async () => {
  try {
        const response = await api.get(`/api/orders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error.response || error);
    throw error;
  }
};

// Fetch all refunds for the logged-in user
export const fetchRefunds = async () => {
  try {
    const response = await api.get(`/api/refunds`);
    return response.data;
  } catch (error) {
    console.error('Error fetching refunds:', error.response || error);
    throw error;
  }
};

// Admin API calls for Vendor Management
export const getVendors = async () => {
  try {
    const response = await api.get(`/api/admin/vendors`);
    return response.data.data; // Assuming the backend sends { success: true, data: vendors }
  } catch (error) {
    console.error("Error fetching vendors:", error.response || error);
    throw error;
  }
};

export const updateVendorVerificationStatus = async (vendorId, verifiedStatus) => {
  try {
    const response = await api.put(`/api/admin/vendors/${vendorId}/verify`, { verified: verifiedStatus });
    return response.data.data; // Assuming the backend sends { success: true, data: updatedVendor }
  } catch (error) {
    console.error("Error updating vendor verification status:", error.response || error);
    throw error;
  }
};

// Export the configured axios instance
export default api;
